import { useRef, useState } from "react";
import { Box, Typography } from "@material-ui/core";
import { UnfoldMore as DragIcon } from "@material-ui/icons";
import { useTranslation } from "react-i18next";

type BeforeAfterSliderProps = {
  beforeUrl: string;
  afterUrl: string;
  /** Frame aspect ratio as a paddingTop %, e.g. "62.5%" for 16:10 (default), "75%" for 4:3. */
  paddingTop?: string;
  height?: number | string;
  rounded?: boolean;
  /** "cover" (default) crops to fill; "contain" shows the whole image (no cropping). */
  objectFit?: "cover" | "contain";
};

/**
 * Drag the divider to wipe between the original ("before", left) and the AI
 * makeover ("after", right). A click (without dragging) snaps fully to one side
 * and back. Touch + mouse.
 *
 * RTL: all horizontal positioning uses INLINE styles (not `sx`) so MUI's
 * stylis-plugin-rtl does NOT flip `left`/`right`. The pointer math is physical
 * (viewport coordinates), so the handle always follows the finger in both LTR/RTL.
 */
export function BeforeAfterSlider({
  beforeUrl,
  afterUrl,
  paddingTop = "62.5%",
  height,
  rounded = true,
  objectFit = "cover"
}: BeforeAfterSliderProps): JSX.Element {
  const { t: translate } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  const movedRef = useRef(false);
  const [position, setPosition] = useState(50); // % from the physical left edge

  const beforeLabel = translate("makeovers.before", "לפני");
  const afterLabel = translate("makeovers.after", "אחרי");

  const updateFromClientX = (clientX: number) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPosition(Math.max(0, Math.min(100, pct)));
  };

  const onPointerDown = (e: React.PointerEvent) => {
    draggingRef.current = true;
    movedRef.current = false;
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!draggingRef.current) return;
    movedRef.current = true;
    updateFromClientX(e.clientX);
  };

  const onPointerUp = () => {
    if (draggingRef.current && !movedRef.current) {
      // a click (no drag) → snap fully to the opposite side
      setPosition((p) => (p > 50 ? 0 : 100));
    }
    draggingRef.current = false;
  };

  return (
    <Box
      ref={containerRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      sx={{
        position: "relative",
        width: "100%",
        ...(height ? { height } : { paddingTop }),
        borderRadius: rounded ? 2 : 0,
        overflow: "hidden",
        cursor: "ew-resize",
        backgroundColor: "grey.200",
        userSelect: "none",
        touchAction: "none"
      }}
    >
      {/* after (base, on the right) */}
      <Box
        component="img"
        src={afterUrl}
        alt={afterLabel}
        draggable={false}
        loading="lazy"
        sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit }}
      />
      {/* before (overlay, clipped to the left `position%`) — clipPath inline so RTL doesn't flip it */}
      <Box
        component="img"
        src={beforeUrl}
        alt={beforeLabel}
        draggable={false}
        loading="lazy"
        style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}
        sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit }}
      />

      {/* labels — positioned with inline left/right so they stay physical in RTL */}
      <Box style={{ left: 8 }} sx={{ position: "absolute", top: 8, px: 1, py: 0.25, borderRadius: 1, backgroundColor: "rgba(0,0,0,0.6)", color: "common.white" }}>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          {beforeLabel}
        </Typography>
      </Box>
      <Box style={{ right: 8 }} sx={{ position: "absolute", top: 8, px: 1, py: 0.25, borderRadius: 1, backgroundColor: "primary.main", color: "common.white" }}>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          {afterLabel}
        </Typography>
      </Box>

      {/* divider + handle — inline left so they track the pointer physically in RTL */}
      <Box
        style={{ left: `${position}%`, transform: "translateX(-1px)" }}
        sx={{ position: "absolute", top: 0, bottom: 0, width: 2, backgroundColor: "common.white", boxShadow: 1 }}
      />
      <Box
        style={{ left: `${position}%`, transform: "translate(-50%, -50%)" }}
        sx={{
          position: "absolute",
          top: "50%",
          width: 36,
          height: 36,
          borderRadius: "50%",
          backgroundColor: "common.white",
          boxShadow: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "primary.main"
        }}
      >
        <DragIcon sx={{ transform: "rotate(90deg)" }} fontSize="small" />
      </Box>
    </Box>
  );
}

export default BeforeAfterSlider;
